import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Snackbar,
  Alert,
  Menu,
  MenuItem,
  Chip
} from "@mui/material";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import EmailIcon from "@mui/icons-material/Email";
import SendIcon from "@mui/icons-material/Send";
import KeyIcon from "@mui/icons-material/VpnKey";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";

import NewUser from "./EnterpriseUsers/NewUser";
import GenerateTempKey from "./EnterpriseUsers/GenerateTemporaryKey";
import EditDeleteUsers from "./EnterpriseUsers/EditDeleteUsers";

const EnterpriseUsers = () => {
  const [openNewUser, setOpenNewUser] = useState(false);
  const [usuarios, setUsuarios] = useState([]);
  const [snack, setSnack] = useState({ open: false, msg: "", type: "success" });

  const [anchorEl, setAnchorEl] = useState(null);
  const [usuarioSeleccionado, setUsuarioSeleccionado] = useState(null);

  const [openGenerate, setOpenGenerate] = useState(false);

  const [openEditDelete, setOpenEditDelete] = useState(false);
  const [editDeleteMode, setEditDeleteMode] = useState("edit");

  const cargarUsuarios = async () => {
    try {
      const res = await fetch("/sistema/listar-usuarios/");
      const data = await res.json();
      setUsuarios(data.usuarios || []);
    } catch {
      setUsuarios([]);
    }
  };

  const getCSRF = async () => {
    const r = await fetch("/sistema/get-csrf-token/");
    const d = await r.json();
    return d.csrfToken;
  };

  useEffect(() => {
    cargarUsuarios();
  }, []);

  const abrirMenuEnvio = (e, u) => {
    setAnchorEl(e.currentTarget);
    setUsuarioSeleccionado(u);
  };

  const cerrarMenu = () => {
    setAnchorEl(null);
    setUsuarioSeleccionado(null);
  };

  const enviarWhatsApp = (u) => {
    const numero = u?.telefono || "";
    const mensaje = encodeURIComponent(
      `Hola ${u?.nombre || ""},\n\nCorreo: ${u?.email || ""}\nClave temporal: ${u?.clave_temporal || ""}\n\nAccede aquí:\nhttps://aherrera.ashtech.com.ec/`
    );
    window.open(`https://wa.me/593${numero}?text=${mensaje}`, "_blank");
    setSnack({ open: true, msg: "Clave enviada por WhatsApp", type: "info" });
    cerrarMenu();
  };

  const enviarCorreo = async (u) => {
    try {
      const csrf = await getCSRF();
      const res = await fetch("/sistema/enviar-clave-correo/", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json", "X-CSRFToken": csrf },
        body: JSON.stringify({ id: u.id })
      });
      const d = await res.json();
      if (d.ok) setSnack({ open: true, msg: "Clave enviada por correo", type: "success" });
      else setSnack({ open: true, msg: "Error al enviar correo", type: "error" });
    } catch {
      setSnack({ open: true, msg: "Error de conexión", type: "error" });
    }
    cerrarMenu();
  };

  const mostrarRol = (rol) => {
    if (rol === "SUPER") return "Superusuario";
    if (rol === "ADMIN") return "Administrador";
    if (rol === "VENDOR") return "Vendedor";
    return "Usuario";
  };

  const abrirGenerarClave = (u) => {
    setUsuarioSeleccionado(u);
    setOpenGenerate(true);
  };

  const abrirEditar = (u) => {
    setUsuarioSeleccionado(u);
    setEditDeleteMode("edit");
    setOpenEditDelete(true);
  };

  const abrirEliminar = (u) => {
    setUsuarioSeleccionado(u);
    setEditDeleteMode("delete");
    setOpenEditDelete(true);
  };

  return (
    <Box>
      <Typography variant="h6" sx={{ fontWeight: "bold", color: "#1565C0", mb: 2 }}>
        Gestión de Usuarios del Sistema
      </Typography>

      <Button
        variant="contained"
        onClick={() => setOpenNewUser(true)}
        sx={{
          backgroundColor: "#1565C0",
          "&:hover": { backgroundColor: "#0D47A1" },
          textTransform: "none",
          mb: 3
        }}
      >
        Crear nuevo usuario
      </Button>

      <Paper sx={{ overflow: "hidden", borderRadius: "10px" }}>
        <Table size="small">
          <TableHead sx={{ backgroundColor: "#1565C0" }}>
            <TableRow>
              <TableCell sx={{ color: "#fff", fontWeight: "bold" }}>Nombre</TableCell>
              <TableCell sx={{ color: "#fff", fontWeight: "bold" }}>Correo</TableCell>
              <TableCell sx={{ color: "#fff", fontWeight: "bold" }}>Teléfono</TableCell>
              <TableCell sx={{ color: "#fff", fontWeight: "bold" }}>Rol</TableCell>
              <TableCell sx={{ color: "#fff", fontWeight: "bold" }}>Estado</TableCell>
              <TableCell sx={{ color: "#fff", fontWeight: "bold" }}>Clave Temporal</TableCell>
              <TableCell sx={{ color: "#fff", fontWeight: "bold" }}>Acciones</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {usuarios.map((u) => (
              <TableRow key={u.id}>
                <TableCell>{u.nombre}</TableCell>
                <TableCell>{u.email}</TableCell>
                <TableCell>{u.telefono}</TableCell>
                <TableCell>{mostrarRol(u.rol)}</TableCell>

                <TableCell>
                  <Chip
                    label={u.activo ? "Activo" : "Inactivo"}
                    size="small"
                    sx={{
                      fontWeight: 700,
                      bgcolor: u.activo ? "#E8F5E9" : "#FFEBEE",
                      color: u.activo ? "#2E7D32" : "#C62828"
                    }}
                  />
                </TableCell>

                <TableCell>
                  {u.clave_temporal ? (
                    <Chip label="Pendiente" size="small" sx={{ fontWeight: 700 }} color="warning" />
                  ) : (
                    <Chip label="Actualizada" size="small" sx={{ fontWeight: 700 }} />
                  )}
                </TableCell>

                <TableCell>
                  {u.clave_temporal ? (
                    <Tooltip title="Enviar clave temporal">
                      <IconButton
                        onClick={(e) => abrirMenuEnvio(e, u)}
                        sx={{ bgcolor: "#E3F2FD", mr: 1 }}
                      >
                        <SendIcon sx={{ color: "#1565C0" }} />
                      </IconButton>
                    </Tooltip>
                  ) : (
                    <Tooltip title="Generar nueva clave temporal">
                      <IconButton
                        onClick={() => abrirGenerarClave(u)}
                        sx={{ bgcolor: "#F3E5F5", mr: 1 }}
                      >
                        <KeyIcon sx={{ color: "#7B1FA2" }} />
                      </IconButton>
                    </Tooltip>
                  )}

                  <Tooltip title="Editar usuario">
                    <IconButton onClick={() => abrirEditar(u)} sx={{ bgcolor: "#E3F2FD", mr: 1 }}>
                      <EditIcon sx={{ color: "#1565C0" }} />
                    </IconButton>
                  </Tooltip>

                  <Tooltip title="Eliminar usuario">
                    <IconButton onClick={() => abrirEliminar(u)} sx={{ bgcolor: "#FFEBEE" }}>
                      <DeleteIcon sx={{ color: "#C62828" }} />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={cerrarMenu}>
        <MenuItem onClick={() => enviarWhatsApp(usuarioSeleccionado)}>
          <WhatsAppIcon sx={{ color: "green", mr: 1 }} /> WhatsApp
        </MenuItem>
        <MenuItem onClick={() => enviarCorreo(usuarioSeleccionado)}>
          <EmailIcon sx={{ color: "#1565C0", mr: 1 }} /> Correo
        </MenuItem>
      </Menu>

      <NewUser
        open={openNewUser}
        onClose={() => setOpenNewUser(false)}
        onCreated={async () => {
          await cargarUsuarios();
          setSnack({ open: true, msg: "Usuario creado correctamente", type: "success" });
        }}
      />

      <GenerateTempKey
        open={openGenerate}
        user={usuarioSeleccionado}
        onClose={() => setOpenGenerate(false)}
        getCSRF={getCSRF}
        onDone={async (msg, type = "success") => {
          await cargarUsuarios();
          setSnack({ open: true, msg: msg || "Clave temporal generada", type });
        }}
      />

      <EditDeleteUsers
        open={openEditDelete}
        mode={editDeleteMode}
        user={usuarioSeleccionado}
        onClose={() => setOpenEditDelete(false)}
        getCSRF={getCSRF}
        onDone={async (msg, type = "success") => {
          await cargarUsuarios();
          setSnack({ open: true, msg: msg || "Actualizado", type });
        }}
      />

      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert severity={snack.type} sx={{ width: "100%" }}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default EnterpriseUsers;
